import { useState, useEffect, useCallback } from "react";

interface Settings {
  automationEnabled: boolean;
  dryRun: boolean;
  maxConcurrentPublications: number;
  minIntervalMs: number;
  allowedHoursStart: number;
  allowedHoursEnd: number;
  duplicatePolicy: string;
  approvalRequired: boolean;
  logRetentionDays: number;
  timezone: string;
}

export function SettingsPage() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("affiliate_flow_auth_token");
      const response = await fetch("/api/v1/settings", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await response.json();
      if (data.success) {
        setSettings(data.data);
      }
    } catch {
      setError("Erro ao carregar configurações.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const token = localStorage.getItem("affiliate_flow_auth_token");
      const response = await fetch("/api/v1/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(settings),
      });
      const data = await response.json();
      if (data.success) {
        setSettings(data.data);
        setMessage("Configurações salvas com sucesso.");
      } else {
        setError(data.error || "Erro ao salvar.");
      }
    } catch {
      setError("Erro de conexão.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="h-64 animate-pulse rounded-lg bg-muted" />;
  }

  if (!settings) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
        Não foi possível carregar as configurações.
      </div>
    );
  }

  return (
    <div className="max-w-xl space-y-4">
      {error && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}
      {message && (
        <div className="rounded-md border border-green-500/40 bg-green-500/10 p-3 text-sm text-green-700">
          {message}
        </div>
      )}

      <div className="rounded-lg border bg-card p-5">
        <h3 className="mb-4 font-medium">Automação</h3>
        <div className="space-y-3">
          <label className="flex items-center justify-between">
            <span className="text-sm">Automação habilitada</span>
            <input
              type="checkbox"
              checked={settings.automationEnabled}
              onChange={(e) => setSettings({ ...settings, automationEnabled: e.target.checked })}
              className="rounded"
            />
          </label>
          <label className="flex items-center justify-between">
            <span className="text-sm">Modo simulação (dry-run)</span>
            <input
              type="checkbox"
              checked={settings.dryRun}
              onChange={(e) => setSettings({ ...settings, dryRun: e.target.checked })}
              className="rounded"
            />
          </label>
          <label className="flex items-center justify-between">
            <span className="text-sm">Exigir aprovação manual</span>
            <input
              type="checkbox"
              checked={settings.approvalRequired}
              onChange={(e) => setSettings({ ...settings, approvalRequired: e.target.checked })}
              className="rounded"
            />
          </label>
        </div>
      </div>

      <div className="rounded-lg border bg-card p-5">
        <h3 className="mb-4 font-medium">Limites de Envio</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm">Máx. publicações simultâneas</label>
            <input
              type="number"
              min={1}
              max={20}
              value={settings.maxConcurrentPublications}
              onChange={(e) => setSettings({ ...settings, maxConcurrentPublications: parseInt(e.target.value) || 1 })}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm">Intervalo mínimo (ms)</label>
            <input
              type="number"
              min={1000}
              step={1000}
              value={settings.minIntervalMs}
              onChange={(e) => setSettings({ ...settings, minIntervalMs: parseInt(e.target.value) || 1000 })}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm">Horário início (h)</label>
            <input
              type="number"
              min={0}
              max={23}
              value={settings.allowedHoursStart}
              onChange={(e) => setSettings({ ...settings, allowedHoursStart: parseInt(e.target.value) || 0 })}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm">Horário fim (h)</label>
            <input
              type="number"
              min={0}
              max={23}
              value={settings.allowedHoursEnd}
              onChange={(e) => setSettings({ ...settings, allowedHoursEnd: parseInt(e.target.value) || 23 })}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-card p-5">
        <h3 className="mb-4 font-medium">Políticas</h3>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm">Política de duplicatas</label>
            <select
              value={settings.duplicatePolicy}
              onChange={(e) => setSettings({ ...settings, duplicatePolicy: e.target.value })}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value="ignore">Ignorar</option>
              <option value="republish">Republicar</option>
              <option value="notify">Notificar</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm">Retenção de logs (dias)</label>
            <input
              type="number"
              min={1}
              max={365}
              value={settings.logRetentionDays}
              onChange={(e) => setSettings({ ...settings, logRetentionDays: parseInt(e.target.value) || 30 })}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
      >
        {saving ? "Salvando..." : "Salvar Configurações"}
      </button>
    </div>
  );
}
